import React from 'react'
import { inject, observer } from 'mobx-react'
import { DragDropProvider } from '@dnd-kit/react'
import { move } from '@dnd-kit/helpers'
import {
  ColourPicker,
  ColourSwatch,
  ConfirmDialog,
  DeleteIcon,
  PencilIcon,
  SortableRow
} from './ManageModalControls.jsx'

const CountBadge = ({ count, label }) => (
  <div className='manage-modal__counts'>
    <span>{count || 0} {label}</span>
  </div>
)

const ManageStatusContent = ({ manageModalStore }) => {
  const statusStore = manageModalStore.status
  const {
    statusType, displayMode, items, loading, saving, displayModeSaving, error,
    showAdd, newName, newColour, editingId, editingName, picker, deleteItem,
    colourChange, countField, countLabel
  } = statusStore
  const [hoveredId, setHoveredId] = React.useState(null)
  const addInputRef = React.useRef(null)
  const editInputRef = React.useRef(null)

  React.useEffect(() => {
    if (showAdd && addInputRef.current) addInputRef.current.focus()
  }, [showAdd])

  React.useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus()
  }, [editingId])

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      if (deleteItem || colourChange || picker) {
        event.stopImmediatePropagation()
        statusStore.closeOverlays()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [deleteItem, colourChange, picker])

  React.useEffect(() => {
    if (picker == null) return undefined
    const onMouseDown = (event) => {
      if (event.target.closest('.manage-modal__colour-picker, .manage-modal__status-colour')) return
      statusStore.setPicker(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [picker])

  const handleAddStatus = (event) => {
    event.preventDefault()
    statusStore.addStatus()
  }

  return (
    <div className='manage-modal__body manage-modal__body--status'>
      <div className='manage-modal__segment manage-modal__segment--wide' role='group' aria-label='Status type'>
        <button
          type='button'
          className={statusType === 'shape' ? 'active' : ''}
          onClick={() => statusStore.setStatusType('shape')}
        >
          Shape statuses
        </button>
        <button
          type='button'
          className={statusType === 'folder' ? 'active' : ''}
          onClick={() => statusStore.setStatusType('folder')}
        >
          Folder statuses
        </button>
      </div>

      <div className='manage-modal__status-actions'>
        <p>Drag to reorder. Changes apply across PopEx.</p>
        <button
          type='button'
          className='manage-modal__add-status'
          onClick={() => statusStore.openAdd()}
        >
          Add status
        </button>
      </div>

      <div className='manage-modal__display-row'>
        <span>Display colour as</span>
        <div className='manage-modal__segment manage-modal__segment--display' role='group' aria-label='Display colour as'>
          <button
            type='button'
            className={displayMode === 'fill' ? 'active' : ''}
            onClick={() => statusStore.changeDisplayMode('fill')}
            disabled={displayModeSaving}
          >
            Fill
          </button>
          <button
            type='button'
            className={displayMode === 'outline' ? 'active' : ''}
            onClick={() => statusStore.changeDisplayMode('outline')}
            disabled={displayModeSaving}
          >
            Outline
          </button>
        </div>
      </div>

      {error && <p className='manage-modal__error'>{error}</p>}

      {showAdd && (
        <form className='manage-modal__add-row' onSubmit={handleAddStatus}>
          <div className='manage-modal__add-row-inner'>
            <ColourSwatch
              color={newColour}
              colorType={displayMode}
              onClick={() => statusStore.togglePicker('add')}
              ariaLabel='Choose new status colour'
            />
            <input
              className='glass'
              ref={addInputRef}
              value={newName}
              onChange={event => statusStore.setNewName(event.target.value)}
              placeholder='New status'
              aria-label='New status'
              maxLength={255}
            />
            <button type='submit' disabled={saving || !newName.trim()}>Add</button>
          </div>
          {picker === 'add' && (
            <ColourPicker selected={newColour} onSelect={colour => statusStore.selectColour(colour)} />
          )}
        </form>
      )}

      {loading
        ? (
          <p className='manage-modal__hint'>Loading statuses...</p>
          )
        : (
          <DragDropProvider onDragEnd={event => statusStore.reorder(move(items.slice(), event))}>
            <ul className='manage-modal__list manage-modal__list--status'>
              {items.length === 0 && (
              <li className='manage-modal__hint' style={{ listStyle: 'none', border: 0, padding: 0 }}>
                No statuses yet. Click Add status to create one.
              </li>
              )}
              {items.map((item, index) => (
                <SortableRow
                  key={item.id}
                  id={item.id}
                  index={index}
                  className={`manage-modal__row ${hoveredId === item.id ? 'is-hovered' : ''}`}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(current => current === item.id ? null : current)}
                >
                  <div className='manage-modal__colour-wrap'>
                    <ColourSwatch
                      color={item.color}
                      colorType={displayMode}
                      onClick={() => statusStore.togglePicker(item.id)}
                      ariaLabel={`Change ${item.name} colour`}
                    />
                    {picker === item.id && (
                    <ColourPicker selected={item.color} onSelect={colour => statusStore.selectColour(colour)} />
                    )}
                  </div>
                  {editingId === item.id
                    ? (
                      <input
                        ref={editInputRef}
                        className='manage-modal__name-input glass'
                        value={editingName}
                        onChange={event => statusStore.setEditingName(event.target.value)}
                        onBlur={() => statusStore.saveEdit()}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault()
                            event.currentTarget.blur()
                          }
                          if (event.key === 'Escape') {
                            statusStore.setEditingId(null)
                          }
                        }}
                        aria-label='Status name'
                      />
                      )
                    : (
                      <button
                        type='button'
                        className='manage-modal__item-name'
                        onClick={() => statusStore.startEdit(item)}
                      >
                        {item.name}
                      </button>
                      )}
                  {hoveredId === item.id && editingId !== item.id && (
                  <button
                    type='button'
                    className='manage-modal__edit'
                    onClick={() => statusStore.startEdit(item)}
                    aria-label={`Rename ${item.name}`}
                  >
                    <PencilIcon />
                  </button>
                  )}
                  <CountBadge count={item[countField]} label={countLabel} />
                  <button
                    type='button'
                    className='manage-modal__delete'
                    aria-label={`Delete ${item.name} status`}
                    onClick={() => statusStore.setDeleteItem(item)}
                  >
                    <DeleteIcon />
                  </button>
                </SortableRow>
              ))}
            </ul>
          </DragDropProvider>
          )}

      {deleteItem && (
        <ConfirmDialog
          title='Delete status?'
          confirmLabel='Delete'
          confirmKind='danger'
          busy={saving}
          onCancel={() => statusStore.closeOverlays()}
          onConfirm={() => statusStore.confirmDelete()}
        >
          <p>
            Deleting '{deleteItem.name}' will remove it from {deleteItem[countField] || 0} {countLabel}. This action updates filters immediately.
          </p>
        </ConfirmDialog>
      )}

      {colourChange && (
        <ConfirmDialog
          title='Update colour globally?'
          confirmLabel='Confirm'
          confirmKind='navy'
          variant='colour'
          busy={saving}
          onCancel={() => statusStore.closeOverlays()}
          onConfirm={() => statusStore.confirmColourChange()}
        >
          <p>This change updates the status colour everywhere it is used, including map fills, outlines and filters.</p>
          <div className='manage-modal__colour-preview'>
            <span>{colourChange.item.name}</span>
            <span
              className='manage-modal__status-colour'
              style={{ backgroundColor: colourChange.item.color }}
            />
            <span className='manage-modal__colour-arrow' aria-hidden='true'>→</span>
            <span
              className='manage-modal__status-colour manage-modal__status-colour--next'
              style={{ backgroundColor: colourChange.nextColor }}
            />
          </div>
          <p>
            Applies to {colourChange.item[countField] || 0} {countLabel}, including filters.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

export default inject('manageModalStore')(observer(ManageStatusContent))
